import { html, render, nothing, svg, type TemplateResult } from 'lit-html';
import panelStyles from './pk-report-timeseries.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

/**
 * `<pk-report-timeseries>` — line / area / bar over time. v0.7
 *
 * Multi-series support + discrete event overlay markers (§ 5.1, the Datadog
 * standout idea). Fires `pk-range-select` on drag-to-zoom for synchronized
 * zoom (§ 3.5). Inline SVG only — no external charting libs.
 *
 * Attributes:
 *   `mode`             — `line` (default) | `area` | `bar`
 *   `height`           — chart height in px (default 240)
 *   `time-range-from`  — ISO instant, required (x-axis left edge)
 *   `time-range-to`    — ISO instant, required (x-axis right edge)
 *   `y-axis-format`    — `number` (default) | `currency` | `bytes` | `ms` | `percent`
 *   `stacked`          — boolean, stacks areas/bars (default false)
 *   `title`            — optional panel heading
 *
 * Children (slot model):
 *   `<pk-report-timeseries-series>` — a data series with child `<pk-report-timeseries-point>` elements.
 *   `<pk-report-timeseries-overlay>` — a discrete event marker line (§ 5.1).
 *
 * Events:
 *   `pk-point-hover`    — `detail: { series: string, x: string, y: number }`
 *   `pk-overlay-click`  — `detail: { label: string, timestamp: string }`
 *   `pk-range-select`   — `detail: { from: string, to: string }` (drag-to-zoom)
 *
 * Opposing-area variant: for `mode="area"`, if one series has `negative="true"`,
 * its area is mirrored below the zero line (GitHub Pulse pattern for bidirectional
 * metrics such as tokens used vs cached). See `<pk-report-timeseries-series>`.
 *
 * ```html
 * <pk-report-timeseries mode="line" height="240"
 *   time-range-from="2026-05-17T00:00:00Z"
 *   time-range-to="2026-05-24T00:00:00Z">
 *   <pk-report-timeseries-series label="claude-opus-4-7" color="var(--pk-color-info)">
 *     <pk-report-timeseries-point x="2026-05-17T00:00:00Z" y="42.30"></pk-report-timeseries-point>
 *   </pk-report-timeseries-series>
 *   <pk-report-timeseries-overlay
 *     label="Token-throttle policy changed"
 *     timestamp="2026-05-20T14:00:00Z"
 *     color="var(--pk-color-warn)">
 *   </pk-report-timeseries-overlay>
 * </pk-report-timeseries>
 * ```
 */
export class PkReportTimeseries extends HTMLElement {
    static readonly tagName = 'pk-report-timeseries';

    static get observedAttributes(): string[] {
        return ['mode', 'height', 'time-range-from', 'time-range-to',
                'y-axis-format', 'stacked', 'title'];
    }

    private readonly _root: ShadowRoot;
    private readonly _mutationObserver: MutationObserver;

    // Compat data backing fields
    private _seriesData:  PkTimeseriesSeries[] = [];
    private _overlayData: PkTimeseriesOverlay[] = [];

    // Drag state
    private _dragStartX  = -1;
    private _dragCurrentX = -1;
    private _isDragging  = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._mutationObserver = new MutationObserver(() => this._doRender());
    }

    connectedCallback(): void {
        this._upgradeProperty('series');
        this._upgradeProperty('data');
        this._upgradeProperty('overlay');
        this._mutationObserver.observe(this, { childList: true, subtree: true, attributes: true });
        this._doRender();
    }

    // ── Compat data API ────────────────────────────────────────────────────────

    /**
     * Imperative data setter (v0.8 compat). Builds `<pk-report-timeseries-series>`
     * children (each with `<pk-report-timeseries-point>` children). Clears only
     * existing series — overlays are untouched.
     */
    set series(value: PkTimeseriesSeries[] | null | undefined) {
        this._seriesData = Array.isArray(value) ? value : [];
        this._rebuildSeries(this._seriesData);
    }

    get series(): PkTimeseriesSeries[] {
        return this._seriesData;
    }

    /**
     * Imperative data setter (v0.8 compat). Single implicit series from a flat
     * point array. Clears only existing series.
     */
    set data(value: PkTimeseriesPoint[] | null | undefined) {
        const points = Array.isArray(value) ? value : [];
        this._seriesData = [{ label: '', color: 'var(--pk-color-info, #1a73e8)', buckets: points }];
        this._rebuildSeries(this._seriesData);
    }

    get data(): PkTimeseriesSeries[] {
        return this._seriesData;
    }

    /**
     * Imperative data setter (v0.8 compat). Builds `<pk-report-timeseries-overlay>`
     * children. Accepts a single overlay object or an array. Clears only existing
     * overlays — series are untouched.
     */
    set overlay(value: PkTimeseriesOverlay | PkTimeseriesOverlay[] | null | undefined) {
        const arr = value == null ? [] : Array.isArray(value) ? value : [value];
        this._overlayData = arr;
        this._rebuildOverlays();
    }

    get overlay(): PkTimeseriesOverlay[] {
        return this._overlayData;
    }

    private _rebuildSeries(seriesList: PkTimeseriesSeries[]): void {
        this.querySelectorAll('pk-report-timeseries-series').forEach(el => el.remove());
        for (const s of seriesList) {
            const seriesEl = document.createElement('pk-report-timeseries-series');
            seriesEl.setAttribute('label', s.label ?? '');
            if (s.color) seriesEl.setAttribute('color', s.color);
            if (s.negative) seriesEl.setAttribute('negative', 'true');
            (s.buckets ?? []).forEach(o => {
                const t = tsT(o);
                const point = document.createElement('pk-report-timeseries-point');
                point.setAttribute('x', t);
                point.setAttribute('y', String(tsV(o)));
                seriesEl.appendChild(point);
            });
            this.appendChild(seriesEl);
        }
        // MutationObserver fires _doRender on appended children.
    }

    private _rebuildOverlays(): void {
        this.querySelectorAll('pk-report-timeseries-overlay').forEach(el => el.remove());
        for (const ov of this._overlayData) {
            const ovEl = document.createElement('pk-report-timeseries-overlay');
            ovEl.setAttribute('label', ov.label ?? '');
            ovEl.setAttribute('timestamp', ov.timestamp ?? '');
            if (ov.color) ovEl.setAttribute('color', ov.color);
            this.appendChild(ovEl);
        }
    }

    private _upgradeProperty(prop: string): void {
        if (Object.prototype.hasOwnProperty.call(this, prop)) {
            const v: unknown = Reflect.get(this, prop);
            Reflect.deleteProperty(this, prop);
            Reflect.set(this, prop, v);
        }
    }

    disconnectedCallback(): void {
        this._mutationObserver.disconnect();
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    // ── Attribute helpers ─────────────────────────────────────────────────────

    private _mode(): 'line' | 'area' | 'bar' {
        const v = this.getAttribute('mode');
        if (v === 'area') return 'area';
        if (v === 'bar')  return 'bar';
        return 'line';
    }

    private _height(): number {
        const v = parseInt(this.getAttribute('height') ?? '240', 10);
        return isNaN(v) || v <= 0 ? 240 : v;
    }

    private _isStacked(): boolean {
        const v = this.getAttribute('stacked');
        return v === '' || v === 'true';
    }

    private _yFormat(): 'number' | 'currency' | 'bytes' | 'ms' | 'percent' {
        const v = this.getAttribute('y-axis-format') ?? 'number';
        if (['currency','bytes','ms','percent'].includes(v)) return v as 'currency' | 'bytes' | 'ms' | 'percent';
        return 'number';
    }

    // ── Data collection ───────────────────────────────────────────────────────

    private _series(): SeriesData[] {
        return Array.from(this.querySelectorAll('pk-report-timeseries-series')).map(el => {
            const points: PointData[] = Array.from(el.querySelectorAll('pk-report-timeseries-point')).map(pt => ({
                x: pt.getAttribute('x') ?? '',
                y: parseFloat(pt.getAttribute('y') ?? '0') || 0,
            }));
            return {
                label:    el.getAttribute('label') ?? '',
                color:    el.getAttribute('color') ?? 'var(--pk-color-info, #1a73e8)',
                fillArea: el.getAttribute('fill-area') !== 'false',
                negative: el.getAttribute('negative') === 'true',
                points,
            };
        });
    }

    private _overlays(): OverlayData[] {
        return Array.from(this.querySelectorAll('pk-report-timeseries-overlay')).map(el => ({
            label:     el.getAttribute('label') ?? '',
            timestamp: el.getAttribute('timestamp') ?? '',
            color:     el.getAttribute('color') ?? 'var(--pk-color-warn, #e8820a)',
        }));
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    private _layout(allSeries: SeriesData[], height: number): TSLayoutParams {
        const AXIS_LEFT  = 44; // space for y-axis labels
        const AXIS_BTM   = 20; // space for x-axis labels
        const TOTAL_W    = 600;
        const chartW     = TOTAL_W - AXIS_LEFT;
        const chartH     = height - AXIS_BTM;

        // Collect all unique x timestamps, sorted chronologically (x is an ISO
        // timestamp string — sort by parsed time, not lexicographically).
        const xSet = new Set<string>();
        allSeries.forEach(s => s.points.forEach(p => xSet.add(p.x)));
        const xTimes = Array.from(xSet).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime());
        const n = xTimes.length;

        // Time domain from/to
        const fromAttr = this.getAttribute('time-range-from');
        const toAttr   = this.getAttribute('time-range-to');
        const tFrom    = fromAttr ? new Date(fromAttr).getTime() : (n > 0 ? new Date(xTimes[0] ?? '').getTime() : 0);
        const tTo      = toAttr   ? new Date(toAttr).getTime()   : (n > 0 ? new Date(xTimes[n-1] ?? '').getTime() : tFrom + 86400000);
        const tRange   = Math.max(1, tTo - tFrom);

        // Y domain
        const stacked = this._isStacked();
        let yMax = 0;
        let yMin = 0;

        if (stacked) {
            // Max at any x = sum of all non-negative series
            xTimes.forEach(xt => {
                let posSum = 0;
                allSeries.forEach(s => {
                    if (!s.negative) {
                        const pt = s.points.find(p => p.x === xt);
                        if (pt) posSum += pt.y;
                    }
                });
                yMax = Math.max(yMax, posSum);
            });
            allSeries.forEach(s => {
                if (s.negative) s.points.forEach(p => { yMin = Math.min(yMin, -Math.abs(p.y)); });
            });
        } else {
            allSeries.forEach(s => {
                s.points.forEach(p => {
                    const val = s.negative ? -Math.abs(p.y) : p.y;
                    yMax = Math.max(yMax, val);
                    yMin = Math.min(yMin, val);
                });
            });
        }

        if (yMax === yMin) { yMax = yMin + 1; }
        const hasNegative = yMin < 0;
        // Add 10% headroom
        const yPad  = (yMax - yMin) * 0.1;
        const yTop  = yMax + yPad;
        const yBot  = yMin - (hasNegative ? yPad : 0);
        const yRange = Math.max(1, yTop - yBot);

        const toSvgX = (iso: string): number => {
            const t = new Date(iso).getTime();
            return AXIS_LEFT + ((t - tFrom) / tRange) * chartW;
        };
        const toSvgY = (val: number): number => {
            return chartH - ((val - yBot) / yRange) * chartH;
        };
        const zeroY = toSvgY(0);

        return { AXIS_LEFT, AXIS_BTM, TOTAL_W, chartW, chartH,
                 tFrom, tTo, tRange, yTop, yBot, yRange,
                 toSvgX, toSvgY, zeroY, xTimes, hasNegative };
    }

    // ── Y tick formatting ──────────────────────────────────────────────────────

    private _formatY(val: number): string {
        switch (this._yFormat()) {
            case 'currency':
                return val >= 1000 ? `$${(val/1000).toFixed(1)}k` : `$${val.toFixed(2)}`;
            case 'bytes':
                return this._formatBytes(val);
            case 'ms':
                return val >= 1000 ? `${(val/1000).toFixed(1)}s` : `${Math.round(val)}ms`;
            case 'percent':
                return `${val.toFixed(1)}%`;
            default:
                return this._formatNumber(val);
        }
    }

    private _formatBytes(val: number): string {
        if (val >= 1073741824) return `${(val/1073741824).toFixed(1)} GB`;
        if (val >= 1048576)    return `${(val/1048576).toFixed(1)} MB`;
        if (val >= 1024)       return `${(val/1024).toFixed(1)} KB`;
        return `${val.toFixed(0)} B`;
    }

    private _formatNumber(val: number): string {
        if (Math.abs(val) >= 1000000) return `${(val/1000000).toFixed(1)}M`;
        if (Math.abs(val) >= 1000)    return `${(val/1000).toFixed(1)}k`;
        return val % 1 === 0 ? `${val}` : `${val.toFixed(1)}`;
    }

    private _formatX(iso: string): string {
        try {
            const d    = new Date(iso);
            const from = this.getAttribute('time-range-from');
            const to   = this.getAttribute('time-range-to');
            const span = from && to
                ? new Date(to).getTime() - new Date(from).getTime()
                : 7 * 86400000;
            if (span <= 2 * 3600000) {
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (span <= 48 * 3600000) {
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (span <= 90 * 86400000) {
                return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            }
            return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
        } catch {
            return iso;
        }
    }

    // ── Grid / axes ───────────────────────────────────────────────────────────

    private _renderAxes(layout: TSLayoutParams, _allSeries: SeriesData[]): TemplateResult {
        const { AXIS_LEFT, TOTAL_W, chartH, yTop, yBot, toSvgY, zeroY, xTimes, hasNegative } = layout;

        // Y ticks: ~5 evenly-spaced
        const TICK_COUNT = 5;
        const yStep = (yTop - yBot) / TICK_COUNT;
        const yTicks: number[] = [];
        for (let i = 0; i <= TICK_COUNT; i++) yTicks.push(yBot + i * yStep);

        // X ticks: up to 7, sampled from xTimes
        const xSample = xTimes.length <= 7
            ? xTimes
            : xTimes.filter((_, i) => i % Math.ceil(xTimes.length / 7) === 0);

        return svg`
            <!-- Y grid lines + labels -->
            ${yTicks.map(tv => {
                const sy = toSvgY(tv);
                if (sy < 0 || sy > chartH) return nothing;
                return svg`
                    <line class="grid-line" x1="${AXIS_LEFT}" y1="${sy}" x2="${TOTAL_W}" y2="${sy}"></line>
                    <text class="y-axis-label" x="${AXIS_LEFT - 4}" y="${sy}">
                        ${this._formatY(Math.round(tv * 100) / 100)}
                    </text>
                `;
            })}
            <!-- Zero line (only when there are negative values) -->
            ${hasNegative ? svg`
                <line class="zero-line" x1="${AXIS_LEFT}" y1="${zeroY}" x2="${TOTAL_W}" y2="${zeroY}"></line>
            ` : nothing}
            <!-- X axis baseline -->
            <line class="axis-line" x1="${AXIS_LEFT}" y1="${chartH}" x2="${TOTAL_W}" y2="${chartH}"></line>
            <!-- X labels -->
            ${xSample.map(xt => {
                const sx = layout.toSvgX(xt);
                return svg`
                    <text class="x-axis-label" x="${sx}" y="${chartH + 14}">${this._formatX(xt)}</text>
                `;
            })}
        `;
    }

    // ── Line / area rendering ──────────────────────────────────────────────────

    private _buildLinePath(points: PointData[], layout: TSLayoutParams, stackOffsets?: Map<string, number>): string {
        if (points.length === 0) return '';
        const sorted = [...points].sort((a, b) => a.x.localeCompare(b.x));
        return sorted.map((pt, i) => {
            const sx = layout.toSvgX(pt.x);
            const offset = stackOffsets?.get(pt.x) ?? 0;
            const sy = layout.toSvgY(pt.y + offset);
            return `${i === 0 ? 'M' : 'L'} ${sx} ${sy}`;
        }).join(' ');
    }

    private _buildAreaPath(points: PointData[], layout: TSLayoutParams, negative: boolean, stackOffsets?: Map<string, number>): string {
        if (points.length === 0) return '';
        const sorted = [...points].sort((a, b) => a.x.localeCompare(b.x));
        const { zeroY, toSvgX, toSvgY } = layout;

        // Top edge (the actual data values)
        const topPts = sorted.map(pt => {
            const sx = toSvgX(pt.x);
            const offset = stackOffsets?.get(pt.x) ?? 0;
            const val  = negative ? -Math.abs(pt.y) : pt.y;
            const sy   = toSvgY(val + offset);
            return { sx, sy };
        });

        if (topPts.length === 0) return '';

        const top    = topPts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.sx} ${p.sy}`).join(' ');
        // Close path back along the zero line
        const lastPt  = topPts[topPts.length - 1];
        const firstPt = topPts[0];
        if (!lastPt || !firstPt) return top;
        const close = `L ${lastPt.sx} ${zeroY} L ${firstPt.sx} ${zeroY} Z`;
        return `${top} ${close}`;
    }

    private _renderLineSeries(s: SeriesData, layout: TSLayoutParams, _stackOffset?: Map<string, number>): TemplateResult {
        if (s.points.length === 0) return html``;
        const d = this._buildLinePath(s.points, layout, _stackOffset);
        return html`${svg`<path class="series-line" d="${d}" stroke="${s.color}"></path>`}`;
    }

    private _renderAreaSeries(s: SeriesData, layout: TSLayoutParams, stackOffset?: Map<string, number>): TemplateResult {
        if (s.points.length === 0) return html``;
        const linePath = this._buildLinePath(s.points, layout, stackOffset);
        const areaPath = this._buildAreaPath(s.points, layout, s.negative, stackOffset);
        return html`
            ${svg`<path class="series-area ${s.negative ? 'series-area-negative' : ''}" d="${areaPath}" fill="${s.color}"></path>`}
            ${svg`<path class="series-line" d="${linePath}" stroke="${s.color}"></path>`}
        `;
    }

    private _renderBarSeries(allSeries: SeriesData[], layout: TSLayoutParams): TemplateResult {
        const { xTimes, toSvgX, toSvgY, zeroY, AXIS_LEFT } = layout;
        const n = xTimes.length;
        if (n === 0) return html``;

        const totalW    = layout.TOTAL_W - AXIS_LEFT;
        const bucketW   = totalW / n;
        const seriesN   = allSeries.length;
        const barW      = Math.max(2, (bucketW * 0.8) / seriesN);
        const stacked   = this._isStacked();

        const bars = xTimes.flatMap((xt) => {
            const bx = toSvgX(xt);
            let stackY = zeroY;

            return allSeries.map((s, si) => {
                const pt  = s.points.find(p => p.x === xt);
                if (!pt) return nothing;

                const val     = s.negative ? -Math.abs(pt.y) : pt.y;
                const barTop  = toSvgY(val);
                let rx: number, ry: number, rw: number, rh: number;

                if (stacked) {
                    rh     = Math.abs(stackY - barTop);
                    ry     = Math.min(stackY, barTop);
                    rx     = bx - (bucketW * 0.4);
                    rw     = bucketW * 0.8;
                    stackY = barTop;
                } else {
                    const offset = (si - seriesN / 2 + 0.5) * barW;
                    rx = bx + offset - barW / 2;
                    ry = Math.min(barTop, zeroY);
                    rw = barW;
                    rh = Math.abs(barTop - zeroY);
                }

                rh = Math.max(1, rh);

                return svg`
                    <rect class="series-bar-rect"
                          x="${rx}" y="${ry}"
                          width="${Math.max(1, rw)}" height="${rh}"
                          fill="${s.color}"
                          @click="${() => this._firePointHover(s.label, xt, pt.y)}">
                        <title>${s.label}: ${this._formatY(pt.y)} at ${this._formatX(xt)}</title>
                    </rect>
                `;
            });
        });

        return html`${bars}`;
    }

    // ── Event overlays (§ 5.1) ────────────────────────────────────────────────

    private _renderOverlays(overlays: OverlayData[], layout: TSLayoutParams): TemplateResult {
        const { toSvgX, chartH } = layout;

        return html`${overlays.map(ov => {
            let sx: number;
            try {
                sx = toSvgX(ov.timestamp);
            } catch {
                return nothing;
            }

            const LABEL_H  = 14;
            const LABEL_W  = Math.min(120, ov.label.length * 6 + 12);
            const labelY   = 4;

            return svg`
                <g class="overlay-group"
                   role="button"
                   tabindex="0"
                   aria-label="Event: ${ov.label}"
                   @click="${() => this._fireOverlayClick(ov)}"
                   @keydown="${(e: KeyboardEvent) => {
                       if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._fireOverlayClick(ov); }
                   }}">
                    <line class="overlay-line"
                          x1="${sx}" y1="${labelY + LABEL_H}"
                          x2="${sx}" y2="${chartH}"
                          stroke="${ov.color}">
                    </line>
                    <rect class="overlay-label-bg"
                          x="${sx - LABEL_W / 2}" y="${labelY}"
                          width="${LABEL_W}" height="${LABEL_H}"
                          fill="${ov.color}" opacity="0.85">
                    </rect>
                    <text class="overlay-label-text"
                          x="${sx}" y="${labelY + LABEL_H / 2}"
                          fill="#fff"
                          text-anchor="middle">
                        ${ov.label.length > 18 ? ov.label.slice(0, 16) + '…' : ov.label}
                    </text>
                </g>
            `;
        })}`;
    }

    // ── Events ────────────────────────────────────────────────────────────────

    private _firePointHover(series: string, x: string, y: number): void {
        this.dispatchEvent(new CustomEvent('pk-point-hover', {
            bubbles: true, composed: true,
            detail: { series, x, y },
        }));
    }

    private _fireOverlayClick(ov: OverlayData): void {
        this.dispatchEvent(new CustomEvent('pk-overlay-click', {
            bubbles: true, composed: true,
            detail: { label: ov.label, timestamp: ov.timestamp },
        }));
    }

    private _fireRangeSelect(from: string, to: string): void {
        if (!from || !to) return;
        this.dispatchEvent(new CustomEvent('pk-range-select', {
            bubbles: true, composed: true,
            detail: { from, to },
        }));
    }

    // ── Drag-to-zoom ──────────────────────────────────────────────────────────

    private _svgX(e: MouseEvent): number {
        const svgEl = this._root.querySelector<SVGSVGElement>('.timeseries-svg');
        if (!svgEl) return 0;
        const rect = svgEl.getBoundingClientRect();
        const svgW = svgEl.viewBox.baseVal.width || 600;
        return ((e.clientX - rect.left) / rect.width) * svgW;
    }

    private _svgXToIso(svgX: number, layout: TSLayoutParams): string {
        const { AXIS_LEFT, chartW, tFrom, tRange } = layout;
        const relX   = Math.max(0, svgX - AXIS_LEFT);
        const t      = tFrom + (relX / chartW) * tRange;
        return new Date(t).toISOString();
    }

    private _onDragStart(e: MouseEvent): void {
        this._isDragging   = true;
        this._dragStartX   = this._svgX(e);
        this._dragCurrentX = this._dragStartX;
        this._doRender();
    }

    private _onDragMove(e: MouseEvent): void {
        if (!this._isDragging) return;
        this._dragCurrentX = this._svgX(e);
        this._doRender();
    }

    private _onDragEnd(e: MouseEvent, layout: TSLayoutParams): void {
        if (!this._isDragging) return;
        this._isDragging  = false;
        const startX = Math.min(this._dragStartX, this._svgX(e));
        const endX   = Math.max(this._dragStartX, this._svgX(e));
        this._dragStartX   = -1;
        this._dragCurrentX = -1;

        if (endX - startX > 5) {
            this._fireRangeSelect(this._svgXToIso(startX, layout), this._svgXToIso(endX, layout));
        }
        this._doRender();
    }

    private _onDragCancel(): void {
        if (!this._isDragging) return;
        this._isDragging   = false;
        this._dragStartX   = -1;
        this._dragCurrentX = -1;
        this._doRender();
    }

    // ── Legend ────────────────────────────────────────────────────────────────

    private _renderLegend(allSeries: SeriesData[], overlays: OverlayData[]): TemplateResult {
        if (allSeries.length === 0 && overlays.length === 0) return html``;
        return html`
            <div class="legend">
                ${allSeries.map(s => html`
                    <span class="legend-item">
                        <span class="legend-swatch" style="background: ${s.color};"></span>
                        ${s.label}${s.negative ? ' (inverted)' : ''}
                    </span>
                `)}
                ${overlays.map(ov => html`
                    <span class="legend-item">
                        <span class="legend-swatch"
                              style="background: ${ov.color}; border-style: dashed; border-width: 0 0 2px 0;">
                        </span>
                        ${ov.label}
                    </span>
                `)}
            </div>
        `;
    }

    // ── Template ──────────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const title     = this.getAttribute('title');
        const height    = this._height();
        const mode      = this._mode();
        const allSeries = this._series();
        const overlays  = this._overlays();

        const hasData = allSeries.some(s => s.points.length > 0);

        if (!hasData && allSeries.length === 0) {
            return html`
                <style>${sharedTokens}${panelStyles}</style>
                <div class="timeseries-panel">
                    ${title ? html`<div class="timeseries-header"><p class="timeseries-title">${title}</p></div>` : nothing}
                    <div class="empty-state-wrapper"><slot name="empty-state"></slot></div>
                </div>
                <slot style="display:none"></slot>
            `;
        }

        const layout  = this._layout(allSeries, height);
        const { chartH, TOTAL_W } = layout;

        // Build series rendering based on mode
        let seriesContent: TemplateResult;
        const stacked = this._isStacked();

        if (mode === 'bar') {
            seriesContent = this._renderBarSeries(allSeries, layout);
        } else if (mode === 'area') {
            // Compute stack offsets if stacked
            const stackOffsets: Map<string, number> = new Map();
            seriesContent = html`${allSeries.map(s => {
                const offsets = stacked ? new Map(stackOffsets) : undefined;
                const result  = this._renderAreaSeries(s, layout, offsets);
                // Update cumulative stack offsets for next series
                if (stacked && !s.negative) {
                    s.points.forEach(pt => {
                        stackOffsets.set(pt.x, (stackOffsets.get(pt.x) ?? 0) + pt.y);
                    });
                }
                return result;
            })}`;
        } else {
            // line
            seriesContent = html`${allSeries.map(s => this._renderLineSeries(s, layout))}`;
        }

        // Drag overlay
        const dragOverlay = this._isDragging && this._dragStartX >= 0
            ? svg`
                <rect class="drag-select-overlay"
                      x="${Math.min(this._dragStartX, this._dragCurrentX)}"
                      y="0"
                      width="${Math.abs(this._dragCurrentX - this._dragStartX)}"
                      height="${chartH}">
                </rect>
              `
            : nothing;

        // Capture layout for closure (drag end handler needs it)
        const capturedLayout = layout;

        return html`
            <style>${sharedTokens}${panelStyles}</style>
            <div class="timeseries-panel">
                ${title ? html`
                    <div class="timeseries-header">
                        <p class="timeseries-title">${title}</p>
                    </div>
                ` : nothing}
                <div class="timeseries-body">
                    <svg class="timeseries-svg"
                         viewBox="0 0 ${TOTAL_W} ${height}"
                         preserveAspectRatio="none"
                         style="height: ${height}px;"
                         role="img"
                         aria-label="Time series chart"
                         @mousedown="${(e: MouseEvent) => this._onDragStart(e)}"
                         @mousemove="${(e: MouseEvent) => this._onDragMove(e)}"
                         @mouseup="${(e: MouseEvent) => this._onDragEnd(e, capturedLayout)}"
                         @mouseleave="${() => this._onDragCancel()}">
                        ${this._renderAxes(layout, allSeries)}
                        ${seriesContent}
                        ${this._renderOverlays(overlays, layout)}
                        ${dragOverlay}
                    </svg>
                </div>
                ${this._renderLegend(allSeries, overlays)}
            </div>
            <slot style="display:none"></slot>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportTimeseries.tagName)) {
    customElements.define(PkReportTimeseries.tagName, PkReportTimeseries);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-timeseries-series>` — a single data series within `<pk-report-timeseries>`. v0.7
 *
 * Data-only element. Contains `<pk-report-timeseries-point>` children.
 *
 * Attributes:
 *   `label`     — series name for legend (required).
 *   `color`     — CSS color or var() reference (required).
 *   `fill-area` — boolean, `true` = fill under curve (relevant in line mode too). Default true.
 *   `negative`  — boolean. For `mode="area"`: area renders below zero (opposing-area variant).
 *                 Used for bidirectional metrics like "tokens used vs cached" (GitHub Pulse pattern).
 */
export class PkReportTimeseriesSeries extends HTMLElement {
    static readonly tagName = 'pk-report-timeseries-series';

    static get observedAttributes(): string[] {
        return ['label', 'color', 'fill-area', 'negative'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-timeseries')?.dispatchEvent(
            new CustomEvent('_series-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportTimeseriesSeries.tagName)) {
    customElements.define(PkReportTimeseriesSeries.tagName, PkReportTimeseriesSeries);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-timeseries-point>` — a single data point within a series. v0.7
 *
 * Attributes:
 *   `x` — ISO instant (required).
 *   `y` — numeric value (required).
 */
export class PkReportTimeseriesPoint extends HTMLElement {
    static readonly tagName = 'pk-report-timeseries-point';

    static get observedAttributes(): string[] {
        return ['x', 'y'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-timeseries')?.dispatchEvent(
            new CustomEvent('_point-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportTimeseriesPoint.tagName)) {
    customElements.define(PkReportTimeseriesPoint.tagName, PkReportTimeseriesPoint);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-timeseries-overlay>` — a discrete vertical event marker on the chart. v0.7
 *
 * Implements § 5.1 of Reports-Packs-Spec.md — Datadog's event overlay pattern.
 * Renders a dashed vertical line with a label pill at the timestamp.
 * Fires `pk-overlay-click` with `{ label, timestamp }` when clicked.
 *
 * Attributes:
 *   `label`     — human-readable description (required).
 *   `timestamp` — ISO instant at which to draw the marker (required).
 *   `color`     — CSS color or var() reference (default `var(--pk-color-warn)`).
 */
export class PkReportTimeseriesOverlay extends HTMLElement {
    static readonly tagName = 'pk-report-timeseries-overlay';

    static get observedAttributes(): string[] {
        return ['label', 'timestamp', 'color'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-timeseries')?.dispatchEvent(
            new CustomEvent('_overlay-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportTimeseriesOverlay.tagName)) {
    customElements.define(PkReportTimeseriesOverlay.tagName, PkReportTimeseriesOverlay);
}

// ─────────────────────────────────────────────────────────────────────────────

// ── Compat data shapes (CMS-driven imperative API) ───────────────────────────

/** A single time/value point. Defensive key fallbacks tolerate CMS field naming. */
export interface PkTimeseriesPoint {
    t?:     string;
    time?:  string;
    x?:     string;
    date?:  string;
    v?:     number;
    value?: number;
    count?: number;
    y?:     number;
}

/** A named series for the `series` setter. */
export interface PkTimeseriesSeries {
    label?:    string;
    color?:    string;
    buckets?:  PkTimeseriesPoint[];
    negative?: boolean;
}

/** An event-overlay marker for the `overlay` setter. */
export interface PkTimeseriesOverlay {
    label?:     string;
    timestamp?: string;
    color?:     string;
}

function tsT(o: PkTimeseriesPoint): string {
    return o.t ?? o.time ?? o.x ?? o.date ?? '';
}

function tsV(o: PkTimeseriesPoint): number {
    const v = o.v ?? o.value ?? o.count ?? o.y ?? 0;
    return typeof v === 'number' && isFinite(v) ? v : 0;
}

interface PointData {
    x: string;
    y: number;
}

interface SeriesData {
    label:    string;
    color:    string;
    fillArea: boolean;
    negative: boolean;
    points:   PointData[];
}

interface OverlayData {
    label:     string;
    timestamp: string;
    color:     string;
}

interface TSLayoutParams {
    AXIS_LEFT:   number;
    AXIS_BTM:    number;
    TOTAL_W:     number;
    chartW:      number;
    chartH:      number;
    tFrom:       number;
    tTo:         number;
    tRange:      number;
    yTop:        number;
    yBot:        number;
    yRange:      number;
    toSvgX:      (iso: string) => number;
    toSvgY:      (val: number) => number;
    zeroY:       number;
    xTimes:      string[];
    hasNegative: boolean;
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-timeseries':         PkReportTimeseries;
        'pk-report-timeseries-series':  PkReportTimeseriesSeries;
        'pk-report-timeseries-point':   PkReportTimeseriesPoint;
        'pk-report-timeseries-overlay': PkReportTimeseriesOverlay;
    }
}
